/**
 * ledgers.js — the account a posting actually names.
 *
 * A LEDGER is one named account: "HDFC Bank — 4471", "Electricity", "ABC
 * Traders", "Owner's Capital". Every accounting entry in the application names
 * a ledger and nothing else, which is what makes a Trial Balance possible: the
 * trial balance IS the list of ledgers with their balances, and if that list is
 * derived from anything other than the postings themselves it is not a trial
 * balance, it is a summary that happens to look like one.
 *
 * THE SIGN CONVENTION, STATED ONCE
 *
 *   A stored balance is a SIGNED INTEGER IN PAISE.
 *   POSITIVE means a DEBIT balance (Dr.).
 *   NEGATIVE means a CREDIT balance (Cr.).
 *
 * That is true for every ledger of every nature, without exception. A customer
 * who owes ₹25,000 has a balance of +2500000. A supplier owed ₹18,500 has
 * -1850000. Sales for the year of ₹40 lakh has -400000000, because sales is a
 * credit balance — and the profit and loss flips the sign for DISPLAY, at the
 * one place where it is presented, rather than storing it flipped.
 *
 * The alternative — storing "positive means whatever is normal for this nature"
 * — is the model the old `Accounts` collection used, and it needs every reader
 * to know the account's type before it can add two numbers together. One
 * convention, applied everywhere, is the only version of this that stays
 * correct as the number of readers grows.
 *
 * Everything here is PURE. The writes live in
 * features/accounting/ledger-repository.js.
 */

import { round } from './money';
import { formatMoney } from './format';
import { validateRequired } from './validate';
import {
  GROUP_FLAGS,
  NATURES,
  indexGroups,
  natureOf,
  resolveFlags,
} from './ledger-groups';

export const SIDES = { DEBIT: 'debit', CREDIT: 'credit' };

export const SIDE_LABEL = {
  [SIDES.DEBIT]: 'Dr.',
  [SIDES.CREDIT]: 'Cr.',
};


/* ------------------------------------------------------------------ *
 * Signed balances
 * ------------------------------------------------------------------ */

/**
 * The signed effect of one posting line on the ledger it names.
 * Debit adds, credit subtracts. That is the whole of it.
 */
export function signedAmount({ side, amount }) {
  const value = Math.abs(round(amount));
  return side === SIDES.CREDIT ? -value : value;
}

/** Split a signed balance back into the Dr / Cr columns a report prints. */
export function toColumns(signed) {
  const value = round(signed);
  return {
    debit: value > 0 ? value : 0,
    credit: value < 0 ? -value : 0,
  };
}

/** Which side a signed balance sits on. Zero is conventionally a debit. */
export const sideOf = (signed) => (round(signed) < 0 ? SIDES.CREDIT : SIDES.DEBIT);

/**
 * A ledger's opening balance as a signed number, from the two fields the
 * opening-balance form collects.
 *
 * Stored as an amount plus a side rather than as a signed number, because that
 * is how it is entered and how it is checked against a previous year's balance
 * sheet — and a form that asks for a negative number to mean a credit is a form
 * that gets it wrong.
 */
export function openingSigned(ledger) {
  const amount = Math.abs(round(ledger?.openingBalance || 0));
  if (amount === 0) return 0;
  return ledger?.openingSide === SIDES.CREDIT ? -amount : amount;
}

/**
 * The current balance of a ledger: what it opened with plus everything posted
 * to it since.
 *
 * `movement` is maintained by the posting engine with `increment()`, so this is
 * a read of two stored fields and not a scan of the ledger.
 */
export function currentBalance(ledger) {
  return openingSigned(ledger) + round(ledger?.movement || 0);
}

/* ------------------------------------------------------------------ *
 * Presentation
 *
 * The specification asks for plain language that keeps the accounting
 * classification rather than replacing it:
 *
 *     Balance: ₹25,000 Receivable (Dr.)
 *     Balance: ₹18,500 Payable (Cr.)
 *     Balance: ₹10,000 (Dr.)
 *
 * So the word is added only where it means something. "Receivable" on an
 * electricity ledger would be nonsense; "(Dr.)" on it is exactly right.
 * ------------------------------------------------------------------ */

/**
 * @param {number} signed   the signed balance in paise
 * @param {Object} [options]
 * @param {boolean} [options.isParty]  add Receivable / Payable
 * @param {boolean} [options.zeroLabel]
 */
export function describeBalance(signed, { isParty = false, zeroLabel = 'Nil' } = {}) {
  const value = round(signed);
  if (value === 0) return zeroLabel;

  const side = sideOf(value);
  const money = formatMoney(Math.abs(value));

  if (isParty) {
    const word = side === SIDES.DEBIT ? 'Receivable' : 'Payable';
    return `${money} ${word} (${SIDE_LABEL[side]})`;
  }
  return `${money} (${SIDE_LABEL[side]})`;
}


/* ------------------------------------------------------------------ *
 * Classification helpers
 *
 * Each takes the ledger and the loaded group tree, because a ledger's
 * behaviour comes from its group and a ledger that answered these questions
 * from its own fields would drift from the group the day somebody moved it.
 * ------------------------------------------------------------------ */

export function ledgerNature(ledger, groupIndex) {
  if (!ledger?.groupId) return NATURES.ASSET;
  return natureOf(ledger.groupId, groupIndex);
}

export function ledgerFlags(ledger, groupIndex) {
  if (!ledger?.groupId) return new Set();
  return resolveFlags(ledger.groupId, groupIndex);
}


/**
 * Build the lookup a screen needs once and reuses for every row.
 *
 * A voucher grid asks "is this a party?" for every line on every keystroke.
 * Walking the group tree each time is a tree walk per cell per render; doing it
 * once per group list is a map lookup.
 */
export function buildLedgerIndex(ledgers = [], groups = []) {
  const groupIndex = indexGroups(groups);
  const byId = new Map();

  ledgers.forEach((ledger) => {
    const flags = ledgerFlags(ledger, groupIndex);
    byId.set(ledger.id, {
      ...ledger,
      nature: ledgerNature(ledger, groupIndex),
      flags,
      isCash: flags.has(GROUP_FLAGS.CASH),
      isBank: flags.has(GROUP_FLAGS.BANK),
      isParty: flags.has(GROUP_FLAGS.PARTY),
      isTax: flags.has(GROUP_FLAGS.TAX),
      isStock: flags.has(GROUP_FLAGS.STOCK),
      balance: currentBalance(ledger),
    });
  });

  return { byId, groupIndex, list: [...byId.values()] };
}

/* ------------------------------------------------------------------ *
 * Options for the pickers
 * ------------------------------------------------------------------ */

/**
 * Ledger options for the voucher grid.
 *
 * The balance is on the option's `meta` line rather than only appearing after
 * selection, because "which HDFC account has the money in it" is answered by
 * looking at the list, and a picker that hides the balance makes the user
 * select one to find out and then go back.
 */
export function ledgerOptions(indexed = [], { filter = null, groupIndex = null } = {}) {
  return indexed
    .filter((ledger) => !ledger.isArchived)
    .filter((ledger) => (filter ? filter(ledger) : true))
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
    .map((ledger) => ({
      value: ledger.id,
      label: ledger.name,
      meta: describeBalance(ledger.balance, { isParty: ledger.isParty, zeroLabel: 'Nil' }),
      keywords: [ledger.name, ledger.aliasOf, ledger.gstin, ledger.phone]
        .filter(Boolean)
        .join(' '),
      nature: ledger.nature,
      isParty: ledger.isParty,
      isCash: ledger.isCash,
      groupId: ledger.groupId,
    }));
}

/* ------------------------------------------------------------------ *
 * Validation
 * ------------------------------------------------------------------ */

export const MAX_LEDGER_NAME = 100;

/**
 * Everything that must be true before a ledger is written.
 *
 * `existingNames` is a Set of lowercase names already in use. Two ledgers
 * called "Electricty" and "Electricity" is a reporting bug that takes a year to
 * notice, and the only cheap moment to catch it is here.
 */
export function validateLedger(values = {}, { existingNames = new Set(), currentId = null } = {}) {
  const errors = {};

  const nameError = validateRequired(values.name, 'Ledger name');
  if (nameError) {
    errors.name = nameError;
  } else {
    const name = String(values.name).trim();
    if (name.length > MAX_LEDGER_NAME) {
      errors.name = `Keep the name under ${MAX_LEDGER_NAME} characters.`;
    } else if (existingNames.has(name.toLowerCase()) && values.id !== currentId) {
      errors.name = 'A ledger with that name already exists. Two spellings of one head is a reporting bug that takes a year to notice.';
    }
  }

  if (!values.groupId) {
    errors.groupId = 'Every ledger belongs under a group. Choose one.';
  }

  const opening = round(values.openingBalance || 0);
  if (opening < 0) {
    errors.openingBalance =
      'Enter the opening balance as a positive amount and pick Dr. or Cr. beside it.';
  }
  if (opening > 0 && !values.openingSide) {
    errors.openingSide = 'Say whether the opening balance is a debit or a credit.';
  }

  if (values.creditPeriodDays !== undefined && values.creditPeriodDays !== null && values.creditPeriodDays !== '') {
    const days = Number(values.creditPeriodDays);
    if (!Number.isFinite(days) || days < 0 || days > 365) {
      errors.creditPeriodDays = 'Credit period must be between 0 and 365 days.';
    }
  }

  return errors;
}

/**
 * A blank ledger, ready for the quick-create form.
 *
 * Deliberately minimal: the specification is explicit that creating a simple
 * expense ledger should take a few seconds, and a form that opens with fourteen
 * empty fields does not.
 */
export function emptyLedger(overrides = {}) {
  return {
    name: '',
    groupId: '',
    openingBalance: 0,
    openingSide: SIDES.DEBIT,
    notes: '',
    isArchived: false,
    ...overrides,
  };
}

/**
 * Which extra fields the quick-create form should show for a group.
 *
 * The rule is that a field appears when it has a use, and not otherwise. A
 * GSTIN on an electricity ledger is a field somebody has to skip past every
 * time; a GSTIN on a supplier is the field the whole invoice depends on.
 */
export function relevantLedgerFields(groupId, groupIndex) {
  const flags = groupId ? resolveFlags(groupId, groupIndex) : new Set();
  return {
    opening: true,
    /* Contact, tax and credit terms belong to a party. */
    contact: flags.has(GROUP_FLAGS.PARTY),
    gstin: flags.has(GROUP_FLAGS.PARTY),
    address: flags.has(GROUP_FLAGS.PARTY),
    creditPeriod: flags.has(GROUP_FLAGS.PARTY),
    creditLimit: flags.has(GROUP_FLAGS.PARTY),
    billwise: flags.has(GROUP_FLAGS.PARTY),
    /* Bank details belong to a bank. */
    bank: flags.has(GROUP_FLAGS.BANK),
    /* A tax ledger needs to say which side of the return it belongs to. */
    taxRole: flags.has(GROUP_FLAGS.TAX),
  };
}

/* ------------------------------------------------------------------ *
 * Running a ledger statement
 * ------------------------------------------------------------------ */

/**
 * Turn a ledger's entries into the rows a ledger statement prints, with a
 * running balance.
 *
 * The opening figure is passed in rather than derived here, because a statement
 * for August opens with July's closing balance and that is a different sum from
 * the ledger's own opening balance. `openingBalanceAsAt` below computes it.
 */
export function runningStatement(entries = [], opening = 0) {
  let balance = round(opening);
  const rows = entries.map((entry) => {
    balance += signedAmount(entry);
    const columns = toColumns(signedAmount(entry));
    return {
      ...entry,
      debit: columns.debit,
      credit: columns.credit,
      balance,
      balanceSide: sideOf(balance),
    };
  });

  const totals = rows.reduce(
    (acc, row) => ({ debit: acc.debit + row.debit, credit: acc.credit + row.credit }),
    { debit: 0, credit: 0 }
  );

  return {
    rows,
    opening: round(opening),
    openingSide: sideOf(opening),
    totalDebit: totals.debit,
    totalCredit: totals.credit,
    closing: balance,
    closingSide: sideOf(balance),
  };
}

/**
 * A ledger's balance immediately before `from`.
 *
 * Takes ALL of the ledger's entries and a cut-off rather than issuing a second
 * query, because the statement screen already holds them: two range queries to
 * produce one opening figure is two indexes, two round trips and two chances
 * for them to disagree.
 */
export function openingBalanceAsAt(ledger, entries = [], from = null) {
  let balance = openingSigned(ledger);
  if (!from) return balance;
  const cutoff = from instanceof Date ? from.getTime() : new Date(from).getTime();

  entries.forEach((entry) => {
    const when = entryTime(entry);
    if (when !== null && when < cutoff) balance += signedAmount(entry);
  });
  return balance;
}

/** Firestore timestamps, Dates and strings all reach reports. Normalise once. */
export function entryTime(entry) {
  const value = entry?.date;
  if (!value) return null;
  if (value instanceof Date) return value.getTime();
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
}
